import React, { useState } from 'react'
import { requestCode, verifyCode, selectContext, setRememberMe } from '../api/auth'
import './Login.css'

/**
 * Shared login screen for clinicians and caregivers.
 * Flow: enter email/phone -> receive code -> enter code -> token.
 * If a contact maps to multiple patients, shows a context picker.
 */
function Login() {
  const [step, setStep] = useState('contact') // contact | code | select
  const [method, setMethod] = useState('email')
  const [contact, setContact] = useState('')
  const [challengeId, setChallengeId] = useState(null)
  const [code, setCode] = useState('')
  const [selectionToken, setSelectionToken] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMeState] = useState(false)

  async function handleRequestCode(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await requestCode(contact.trim(), method)
      setChallengeId(res.challenge_id)
      setStep('code')
    } catch (err) {
      setError(err.message || 'Could not send code')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyCode(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const data = await verifyCode(challengeId, code.trim())
      if (data.token) {
        // Authenticated; AuthProvider picks up the token via the store listener.
        return
      }
      if (data.selection_token) {
        setSelectionToken(data.selection_token)
        setCandidates(data.patients || [])
        setStep('select')
      }
    } catch (err) {
      setError(err.message || 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleSelect(patientId) {
    setError(null)
    setLoading(true)
    try {
      await selectContext(selectionToken, patientId)
      // Token set by store; AuthProvider takes over.
    } catch (err) {
      setError(err.message || 'Could not select context')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Lucena Health</h1>
        <p className="login-subtitle">Secure provider &amp; caregiver access</p>

        {error && <div className="login-error" role="alert">{error}</div>}

        {step === 'contact' && (
          <form onSubmit={handleRequestCode} className="login-form">
            <div className="login-method-toggle">
              <button
                type="button"
                className={method === 'email' ? 'active' : ''}
                onClick={() => setMethod('email')}
              >
                Email
              </button>
              <button
                type="button"
                className={method === 'sms' ? 'active' : ''}
                onClick={() => setMethod('sms')}
              >
                Phone
              </button>
            </div>
            <label htmlFor="contact">
              {method === 'email' ? 'Email address' : 'Phone number'}
            </label>
            <input
              id="contact"
              type={method === 'email' ? 'email' : 'tel'}
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder={method === 'email' ? 'you@example.com' : '+1 555 123 4567'}
              required
              autoFocus
            />
            <div className="login-remember-me">
              <label>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setRememberMeState(checked)
                    setRememberMe(checked)
                  }}
                />
                Remember me (not recommended on shared computers)
              </label>
            </div>
            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? 'Sending…' : 'Send verification code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={handleVerifyCode} className="login-form">
            <label htmlFor="code">Enter the 6-digit code</label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
              autoFocus
            />
            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? 'Verifying…' : 'Verify & sign in'}
            </button>
            <button
              type="button"
              className="login-link"
              onClick={() => { setStep('contact'); setCode('') }}
            >
              Use a different contact
            </button>
          </form>
        )}

        {step === 'select' && (
          <div className="login-form">
            <p>This contact is linked to multiple patients. Choose one:</p>
            <ul className="login-candidates">
              {candidates.map((c) => (
                <li key={c.patient_id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(c.patient_id)}
                    disabled={loading}
                  >
                    {c.masked_name || c.patient_id}
                    {c.masked_dob ? ` — ${c.masked_dob}` : ''}
                    {c.role ? ` (${c.role})` : ''}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default Login
